// ADR-012 (mailing platform) i18n — "email" cluster label catalog. Spread
// into the central LABELS in labels.ts. Backs the four public email-identity
// pages (ForgotPassword / ResetPassword / VerifyEmail / SetPassword), the
// LoginPage "Forgot password?" link, and the UserFormPage email-invite /
// resend-verification additions. EN values are the source copy; HE values are
// the Hebrew translation (this app ships full he UX — RTL is handled globally
// by LangDirection, no per-page dir needed).
export const EMAIL_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Shared across the pre-auth email-identity pages
  // ═══════════════════════════════════════════════════════════════════════
  emailBackToLogin: { en: 'Back to login', he: 'חזרה להתחברות' },
  emailGoToLogin: { en: 'Go to login', he: 'מעבר להתחברות' },
  emailInvalidLinkTitle: { en: 'Link expired or invalid', he: 'הקישור פג תוקף או שגוי' },
  emailInvalidLinkMessage: {
    en: 'This link is no longer valid. It may have expired or already been used — request a new one.',
    he: 'קישור זה אינו תקף עוד. ייתכן שפג תוקפו או שכבר נעשה בו שימוש — בקש קישור חדש.',
  },
  emailInvalidOrExpiredToken: {
    en: 'This link has expired or already been used. Please request a new one.',
    he: 'קישור זה פג תוקף או שכבר נעשה בו שימוש. אנא בקש קישור חדש.',
  },
  emailWeakPassword: {
    en: 'That password does not meet the security requirements.',
    he: 'הסיסמה אינה עומדת בדרישות האבטחה.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  LoginPage — "Forgot password?" link
  // ═══════════════════════════════════════════════════════════════════════
  loginForgotPasswordLink: { en: 'Forgot password?', he: 'שכחת סיסמה?' },

  // ═══════════════════════════════════════════════════════════════════════
  //  ForgotPasswordPage
  // ═══════════════════════════════════════════════════════════════════════
  forgotPwTitle: { en: 'Forgot your password?', he: 'שכחת את הסיסמה?' },
  forgotPwSubtitle: {
    en: "Enter your tenant and email — we'll send you a link to reset your password if the account exists.",
    he: 'הזן את הארגון והאימייל שלך — אם החשבון קיים נשלח אליך קישור לאיפוס הסיסמה.',
  },
  forgotPwSubmit: { en: 'Send reset link', he: 'שלח קישור לאיפוס' },
  forgotPwSubmitting: { en: 'Sending...', he: 'שולח...' },
  forgotPwSuccess: {
    en: 'If the account exists, an email with reset instructions was sent.',
    he: 'אם החשבון קיים, נשלח אליו אימייל עם הוראות לאיפוס הסיסמה.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  ResetPasswordPage
  // ═══════════════════════════════════════════════════════════════════════
  resetPwTitle: { en: 'Reset your password', he: 'איפוס הסיסמה' },
  resetPwSubtitle: { en: 'Choose a new password for your account.', he: 'בחר סיסמה חדשה לחשבון שלך.' },
  resetPwSubmit: { en: 'Reset password', he: 'אפס סיסמה' },
  resetPwSubmitting: { en: 'Resetting...', he: 'מאפס...' },
  resetPwSuccess: {
    en: 'Your password has been reset. You can now sign in with your new password.',
    he: 'הסיסמה שלך אופסה. כעת ניתן להתחבר עם הסיסמה החדשה.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  SetPasswordPage (staff email-invite — authMode==='emailInvite')
  // ═══════════════════════════════════════════════════════════════════════
  setPwTitle: { en: 'Set your password', he: 'הגדרת סיסמה' },
  setPwSubtitle: {
    en: 'Welcome! Choose a password to activate your account.',
    he: 'ברוך הבא! בחר סיסמה כדי להפעיל את החשבון שלך.',
  },
  setPwSubmit: { en: 'Set password', he: 'הגדר סיסמה' },
  setPwSubmitting: { en: 'Setting...', he: 'מגדיר...' },
  setPwSuccess: {
    en: 'Your password has been set. You can now sign in.',
    he: 'הסיסמה שלך הוגדרה. כעת ניתן להתחבר.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  VerifyEmailPage
  // ═══════════════════════════════════════════════════════════════════════
  verifyEmailTitle: { en: 'Verify your email', he: 'אימות האימייל שלך' },
  verifyEmailSubtitle: {
    en: 'Enter the verification code sent to your email.',
    he: 'הזן את קוד האימות שנשלח לאימייל שלך.',
  },
  verifyEmailCodeLabel: { en: 'Verification code', he: 'קוד אימות' },
  verifyEmailSubmit: { en: 'Verify', he: 'אמת' },
  verifyEmailSubmitting: { en: 'Verifying...', he: 'מאמת...' },
  verifyEmailSuccess: { en: 'Your email has been verified.', he: 'האימייל שלך אומת.' },
  verifyEmailInvalidCode: {
    en: 'That code is invalid or has expired. Please request a new one.',
    he: 'הקוד שגוי או שפג תוקפו. אנא בקש קוד חדש.',
  },
  verifyEmailResendLink: { en: 'Resend code', he: 'שלח קוד מחדש' },
  verifyEmailResendSent: {
    en: 'If the account exists and is unverified, a new verification code was sent.',
    he: 'אם החשבון קיים וטרם אומת, נשלח קוד אימות חדש.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  UserFormPage — auth method selector + email-invite + resend verification
  // ═══════════════════════════════════════════════════════════════════════
  userFormAuthModeLabel: { en: 'Authentication method', he: 'שיטת הזדהות' },
  userFormAuthModeTempPassword: {
    en: 'Temporary password (I set it, they change it at first login)',
    he: 'סיסמה זמנית (אני קובע אותה, הם ישנו אותה בכניסה הראשונה)',
  },
  userFormAuthModeEmailInvite: {
    en: 'Email invitation (they set their own password via email link)',
    he: 'הזמנה באימייל (הם קובעים סיסמה בעצמם דרך קישור באימייל)',
  },
  userFormPreferredLanguageLabel: { en: 'Invitation email language', he: 'שפת אימייל ההזמנה' },
  userFormEmailInviteSuccessTitle: { en: 'Invitation sent', he: 'ההזמנה נשלחה' },
  userFormEmailInviteSuccessMessage: {
    en: 'An email was sent with instructions to set a password and verify the account.',
    he: 'נשלח אימייל עם הוראות להגדרת סיסמה ואימות החשבון.',
  },
  userFormEmailInviteSuccessOk: { en: 'OK', he: 'אישור' },
  userFormResendVerificationSectionTitle: { en: 'Email verification', he: 'אימות אימייל' },
  userFormResendVerification: { en: 'Resend verification email', he: 'שלח מחדש אימייל אימות' },
  userFormResendVerificationSending: { en: 'Sending...', he: 'שולח...' },
  userFormResendVerificationSuccess: { en: 'Verification email re-sent.', he: 'אימייל האימות נשלח מחדש.' },
  userFormResendVerificationNote: {
    en: 'You can resend the verification email at any time — it has no effect if the account is already verified.',
    he: 'ניתן לשלוח מחדש את אימייל האימות בכל עת — לא תהיה לכך השפעה אם החשבון כבר אומת.',
  },
  userFormAlreadyVerified: { en: 'This account is already verified.', he: 'החשבון כבר אומת.' },
} satisfies Record<string, { en: string; he: string }>;
